/**
 * Risk Profile Tab Component
 * TASK-179: Investment Risk Profile Workflow
 * TASK-244: Standardized design using shared form review components
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { API_CONFIG } from '../../../config/api-config';
import {
  User, TrendingUp, DollarSign, Target, Shield, AlertCircle,
  FileText, Upload, CheckCircle, Award, Briefcase, FileCheck,
  Edit3, Calendar
} from 'lucide-react';
import { ReviewLayout, ReviewSection, HeaderAction } from '../../forms/review';
import { RiskProfileWizard } from '../../risk-profile/RiskProfileWizard';
import { RiskProfileExtractionReview } from '../../risk-profile/RiskProfileExtractionReview';
import riskProfileService from '../../../services/riskProfileService';
import type { RiskProfileResponse, RiskProfileExtractionResponse } from '../../../services/riskProfileService';
import { toast } from 'sonner';

// Extended type to include pdf_url from API response
type RiskProfileWithPdf = RiskProfileResponse['risk_profile'] & { pdf_url?: string };

interface RiskProfileTabProps {
  clientId: string;
  clientName: string;
}

// Section configuration for Risk Profile
const SECTION_CONFIG = [
  { key: 'section1', id: 'personal', title: 'Personal Information', icon: User },
  { key: 'section2', id: 'knowledge', title: 'Knowledge & Experience', icon: TrendingUp },
  { key: 'section3', id: 'financial', title: 'Financial Situation', icon: DollarSign },
  { key: 'section4', id: 'objectives', title: 'Investment Objectives', icon: Target },
  { key: 'section5', id: 'risk', title: 'Risk Tolerance', icon: Shield },
  { key: 'section6', id: 'profile', title: 'Determined Risk Profile', icon: Award },
  { key: 'section7', id: 'strategy', title: 'Investment Strategy', icon: Briefcase },
  { key: 'section8', id: 'declarations', title: 'Declarations', icon: FileCheck },
  { key: 'section9', id: 'signatures', title: 'Signatures', icon: Edit3 },
  { key: 'section10', id: 'review', title: 'Review Schedule', icon: Calendar },
];

// Required fields per section for validation
// TASK-658: the ONE required-fields source lives in riskProfileService
// (mirrors backend shared.risk.completion).
import { REQUIRED_FIELDS } from '../../../services/riskProfileService';

const RiskProfileTab: React.FC<RiskProfileTabProps> = ({ clientId, clientName }) => {
  const [profileData, setProfileData] = useState<RiskProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractionData, setExtractionData] = useState<RiskProfileExtractionResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchRiskProfile();
  }, [clientId]);

  const fetchRiskProfile = async () => {
    setLoading(true);
    try {
      const profile = await riskProfileService.get(clientId);
      setProfileData(profile);
    } catch (error: any) {
      console.error('Error fetching risk profile:', error);
      if (error.response?.status === 404) {
        setProfileData(null);
      } else {
        toast.error('Failed to load risk profile data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get nested value from object using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Calculate missing fields for a section
  const getMissingFields = (sectionKey: string, sectionData: any): string[] => {
    if (!sectionData) return ['All fields'];

    const required = REQUIRED_FIELDS[sectionKey] || [];
    return required.filter(field => {
      const value = getNestedValue(sectionData, field);
      if (Array.isArray(value)) return value.length === 0;
      return !value;
    }).map(field => {
      // Convert field path to readable label
      return field.split('.').pop()!
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase());
    });
  };

  // Transform profile data to ReviewSection format
  const sections: ReviewSection[] = useMemo(() => {
    if (!profileData?.risk_profile) return [];
    const riskProfile = profileData.risk_profile as Record<string, any>;

    return SECTION_CONFIG.map(({ key, id, title, icon: Icon }) => {
      const sectionData = riskProfile[key] || {};
      const missingFields = getMissingFields(key, sectionData);
      const isComplete = missingFields.length === 0;

      // Extract displayable fields (exclude internal fields)
      const displayData: Record<string, any> = {};
      Object.entries(sectionData).forEach(([fieldKey, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // TASK-471: render the SAC allocation array as a readable {SAC: "x%"} map
          if (fieldKey === 'targetAllocations' && Array.isArray(value)) {
            const table: Record<string, string> = {};
            value.forEach((slot: any) => {
              if (slot?.subAssetClass) table[slot.subAssetClass] = `${slot.targetWeightPct ?? 0}%`;
            });
            displayData[fieldKey] = table;
          } else {
            displayData[fieldKey] = value;
          }
        }
      });

      return {
        id,
        title,
        icon: <Icon className="w-5 h-5" />,
        data: {
          ...displayData,
          complete: isComplete,
          missingFields: isComplete ? undefined : missingFields,
        },
      };
    });
  }, [profileData]);

  const completionPercentage = useMemo(() => {
    if (!profileData?.risk_profile) return 0;
    return riskProfileService.calculateCompletionPercentage(profileData.risk_profile);
  }, [profileData]);

  const handleWizardComplete = async () => {
    fetchRiskProfile();
    setShowWizard(false);
    toast.success('Risk profile saved successfully');
  };

  const handlePDFUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploading(true);
    try {
      const result = await riskProfileService.extractFromPDF(clientId, file);
      setExtractionData(result);
      toast.success('PDF processed successfully', {
        description: `Overall confidence: ${Math.round(result.overall_confidence * 100)}%`
      });
    } catch (error) {
      console.error('Error extracting from PDF:', error);
      toast.error('Failed to process PDF');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadPDF = async () => {
    const riskProfile = profileData?.risk_profile as RiskProfileWithPdf | undefined;
    const pdfUrl = riskProfile?.pdf_url;
    if (!pdfUrl) {
      toast.error('No PDF available. Please generate a PDF first.');
      return;
    }

    try {
      const apiBaseURL = API_CONFIG.GATEWAY_URL;
      const response = await fetch(`${apiBaseURL}${pdfUrl}`, {
        headers: {
          ...(localStorage.getItem('auth_token') && { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }),
          ...(localStorage.getItem('account_id') && { 'X-Account-ID': localStorage.getItem('account_id')! })
        }
      });

      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Risk_Profile_${clientName.replace(' ', '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success('PDF Downloaded!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const result = await riskProfileService.generateBlankPDF(clientId);
      toast.success('PDF generated successfully');

      const apiBaseURL = API_CONFIG.GATEWAY_URL;
      const response = await fetch(`${apiBaseURL}${result.pdf_url}`, {
        headers: {
          ...(localStorage.getItem('auth_token') && { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }),
          ...(localStorage.getItem('account_id') && { 'X-Account-ID': localStorage.getItem('account_id')! })
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = result.filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
      }

      fetchRiskProfile();
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleMarkComplete = async () => {
    try {
      await riskProfileService.complete(clientId);
      toast.success('Risk profile marked as completed');
      fetchRiskProfile();
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error('Failed to mark risk profile as complete');
    }
  };

  // Build header actions
  const headerActions: HeaderAction[] = useMemo(() => {
    const actions: HeaderAction[] = [];
    const riskProfile = profileData?.risk_profile as RiskProfileWithPdf | undefined;

    if (profileData?.status !== 'completed') {
      actions.push({
        id: 'generate',
        label: 'Generate PDF',
        icon: <FileText className="w-4 h-4" />,
        variant: 'secondary',
        onClick: handleGeneratePDF,
      });

      if (riskProfile?.pdf_url) {
        actions.push({
          id: 'complete',
          label: 'Mark Complete',
          icon: <CheckCircle className="w-4 h-4" />,
          variant: 'primary',
          onClick: handleMarkComplete,
        });
      }
    }

    return actions;
  }, [profileData]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-6 animate-pulse">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-gray-200 rounded" />
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-200 rounded w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!profileData || profileData.status === 'not_started') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No Risk Profile data available</p>
        <p className="text-sm text-gray-500 mt-1 mb-6">
          Complete the FinSA/MiFID II compliant investment risk questionnaire
        </p>

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setShowWizard(true)}
            className="px-4 py-2 bg-kielis-gold text-kielis-navy rounded-md hover:bg-opacity-90 font-medium inline-flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Complete Risk Profile
          </button>

          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handlePDFUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Processing...' : 'Upload Signed PDF'}
            </button>
          </div>
        </div>

        {extractionData && (
          <RiskProfileExtractionReview
            extractionData={extractionData}
            clientId={clientId}
            clientName={clientName}
            onAccept={() => { setExtractionData(null); fetchRiskProfile(); setShowWizard(true); }}
            onReject={() => { setExtractionData(null); toast.info('You can fill the form manually'); }}
            onClose={() => setExtractionData(null)}
          />
        )}

        {showWizard && (
          <div className="fixed inset-0 z-50">
            <RiskProfileWizard
              clientId={clientId}
              clientName={clientName}
              onClose={() => setShowWizard(false)}
              onComplete={handleWizardComplete}
            />
          </div>
        )}
      </div>
    );
  }

  const riskProfileForRender = profileData.risk_profile as RiskProfileWithPdf;

  return (
    <>
      <ReviewLayout
        title="Risk Profile Review"
        subtitle="FinSA/MiFID II compliant investment risk assessment"
        sidebarTitle="Risk Profile"
        sections={sections}
        completionPercentage={completionPercentage}
        status={profileData.status === 'completed' ? 'completed' : 'in_progress'}
        lastUpdated={profileData.last_updated || undefined}
        onEdit={() => setShowWizard(true)}
        onDownloadPDF={riskProfileForRender?.pdf_url ? handleDownloadPDF : undefined}
        actions={headerActions}
        footerContent={
          <div className="border border-gray-200 rounded-lg p-4 bg-blue-50 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Upload Signed PDF</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Upload a completed PDF to extract data using AI
                </p>
              </div>
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handlePDFUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Processing...' : 'Upload PDF'}
                </button>
              </div>
            </div>
          </div>
        }
      />

      {extractionData && (
        <RiskProfileExtractionReview
          extractionData={extractionData}
          clientId={clientId}
          clientName={clientName}
          onAccept={() => { setExtractionData(null); fetchRiskProfile(); setShowWizard(true); }}
          onReject={() => { setExtractionData(null); toast.info('You can fill the form manually'); }}
          onClose={() => setExtractionData(null)}
        />
      )}

      {showWizard && (
        <div className="fixed inset-0 z-50">
          <RiskProfileWizard
            clientId={clientId}
            clientName={clientName}
            initialData={profileData?.risk_profile}
            onClose={() => setShowWizard(false)}
            onComplete={handleWizardComplete}
          />
        </div>
      )}
    </>
  );
};

export default RiskProfileTab;
